"use client";

import type { CSSProperties, FC, HTMLAttributes, ReactNode } from "react";
import {
	cloneElement,
	createContext,
	isValidElement,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import {
	canNavigateTo,
	describePaginationStatus,
	getStepTarget,
	type PaginationEllipsisItem,
	type PaginationListItem,
	type PaginationPageItem,
	type PaginationTotalStatus,
	resolvePaginationModel,
} from "./pagination-model";

type PaginationPage = PaginationPageItem;

type PaginationEllipsisType = PaginationEllipsisItem;

type PaginationItemType = PaginationListItem;

type PaginationContextType = {
	pages: PaginationItemType[];
	currentPage: number | null;
	total: number | null;
	totalStatus: PaginationTotalStatus;
	status: ReturnType<typeof describePaginationStatus>;
	canNavigateTo: (page: number) => boolean;
	onPageChange: (page: number) => void;
};

const PaginationContext = createContext<PaginationContextType | undefined>(
	undefined
);

export const DEFAULT_PAGINATION_ARIA_LABEL = "Paginação";

export type PaginationRootProps = {
	/** Number of sibling pages to show on each side of the current page */
	siblingCount?: number;
	/** Current active page number (index 1). */
	page: number;
	total: number | null;
	children: ReactNode;
	/** The style of the pagination root. */
	style?: CSSProperties;
	/** The class name of the pagination root. */
	className?: string;
	ariaLabel?: string;
	/** Callback function that's called when the page changes with the new page number. */
	onPageChange?: (page: number) => void;
};

const isHidden = (element: Element) => {
	const candidate = element as Element & {
		checkVisibility?: () => boolean;
	};
	return typeof candidate.checkVisibility === "function"
		? !candidate.checkVisibility()
		: false;
};

const isUnavailable = (element: Element) =>
	!element.isConnected ||
	(element instanceof HTMLButtonElement && element.disabled) ||
	element.getAttribute("aria-disabled") === "true" ||
	isHidden(element);

const FOCUS_CANDIDATES = [
	"[aria-current='page']",
	"button:not([disabled]):not([aria-disabled='true'])",
];

export function findPaginationFocusTarget(
	nav: HTMLElement
): HTMLElement | null {
	for (const selector of FOCUS_CANDIDATES) {
		const candidates = Array.from(
			nav.querySelectorAll<HTMLElement>(selector)
		).filter((element) => !isUnavailable(element));
		if (candidates.length > 0) {
			return candidates[0];
		}
	}
	return null;
}

const PaginationRoot = ({
	total,
	siblingCount,
	page,
	onPageChange,
	children,
	style,
	className,
	ariaLabel = DEFAULT_PAGINATION_ARIA_LABEL,
}: PaginationRootProps) => {
	const model = useMemo(
		() => resolvePaginationModel({ page, total, siblingCount }),
		[page, total, siblingCount]
	);

	const navRef = useRef<HTMLElement>(null);
	const lastFocusedRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		const nav = navRef.current;
		const lastFocused = lastFocusedRef.current;
		if (!(nav && lastFocused)) {
			return;
		}
		const active = document.activeElement;
		const focusIsOutside =
			active !== null && active !== document.body && !nav.contains(active);
		if (focusIsOutside) {
			lastFocusedRef.current = null;
			return;
		}
		const lostFocus =
			active === null ||
			active === document.body ||
			(nav.contains(active) && isUnavailable(active));
		if (!(lostFocus && isUnavailable(lastFocused))) {
			return;
		}
		const target = findPaginationFocusTarget(nav);
		if (target) {
			target.focus({ preventScroll: true });
			lastFocusedRef.current = target;
		} else {
			lastFocusedRef.current = null;
		}
	});

	useEffect(() => {
		const nav = navRef.current;
		if (!nav) {
			return;
		}
		const handleFocusIn = (event: FocusEvent) => {
			if (event.target instanceof HTMLElement) {
				lastFocusedRef.current = event.target;
			}
		};
		const handleFocusOut = (event: FocusEvent) => {
			const next = event.relatedTarget;
			if (next instanceof Node && !nav.contains(next)) {
				lastFocusedRef.current = null;
			}
		};
		nav.addEventListener("focusin", handleFocusIn);
		nav.addEventListener("focusout", handleFocusOut);
		return () => {
			nav.removeEventListener("focusin", handleFocusIn);
			nav.removeEventListener("focusout", handleFocusOut);
		};
	}, []);

	const paginationContextValue: PaginationContextType = {
		pages: model.items,
		currentPage: model.page,
		total: model.total,
		totalStatus: model.totalStatus,
		status: describePaginationStatus(model),
		canNavigateTo: (target) => canNavigateTo(model, target),
		onPageChange: (target) => {
			if (canNavigateTo(model, target)) {
				onPageChange?.(target);
			}
		},
	};

	return (
		<PaginationContext.Provider value={paginationContextValue}>
			<nav
				aria-label={ariaLabel}
				className={className}
				ref={navRef}
				style={style}
			>
				{children}
			</nav>
		</PaginationContext.Provider>
	);
};

const usePaginationContext = () => {
	const context = useContext(PaginationContext);
	if (!context) {
		throw new Error(
			"Pagination components must be used within a Pagination.Root"
		);
	}
	return context;
};

type TriggerRenderProps = {
	isDisabled: boolean;
	onClick: () => void;
};

type TriggerProps = {
	/** The children of the trigger. Can be a render prop or a valid element. */
	children: ReactNode | ((props: TriggerRenderProps) => ReactNode);
	/** The style of the trigger. */
	style?: CSSProperties;
	/** The class name of the trigger. */
	className?: string | ((args: { isDisabled: boolean }) => string);
	/** If true, the child element will be cloned and passed down the prop of the trigger. */
	asChild?: boolean;
	/** The direction of the trigger. */
	direction: "prev" | "next";
	/** The aria label of the trigger. */
	ariaLabel?: string;
};

const Trigger: FC<TriggerProps> = ({
	children,
	style,
	className,
	asChild = false,
	direction,
	ariaLabel,
}) => {
	const { currentPage, total, onPageChange } = usePaginationContext();

	const target = getStepTarget({ page: currentPage, total }, direction);
	const isDisabled = target === null;

	const handleClick = () => {
		if (target !== null) {
			onPageChange(target);
		}
	};

	const computedClassName =
		typeof className === "function" ? className({ isDisabled }) : className;

	const defaultAriaLabel =
		direction === "prev" ? "Página anterior" : "Próxima página";

	// If the children is a render prop, we need to pass the isDisabled and onClick to the render prop.
	if (typeof children === "function") {
		return <>{children({ isDisabled, onClick: handleClick })}</>;
	}

	// If the children is a valid element, we need to clone it and pass the isDisabled and onClick to the cloned element.
	if (asChild && isValidElement(children)) {
		return cloneElement(children, {
			onClick: handleClick,
			disabled: isDisabled,
			isDisabled,
			"aria-label": ariaLabel || defaultAriaLabel,
			style: {
				...(children.props as HTMLAttributes<HTMLElement>).style,
				...style,
			},
			className:
				[
					computedClassName,
					(children.props as HTMLAttributes<HTMLElement>).className,
				]
					.filter(Boolean)
					.join(" ") || undefined,
		} as HTMLAttributes<HTMLElement>);
	}

	return (
		<button
			aria-label={ariaLabel || defaultAriaLabel}
			className={computedClassName}
			disabled={isDisabled}
			onClick={handleClick}
			style={style}
			type="button"
		>
			{children}
		</button>
	);
};

const PaginationPrevTrigger: FC<Omit<TriggerProps, "direction">> = (props) => (
	<Trigger {...props} direction="prev" />
);

const PaginationNextTrigger: FC<Omit<TriggerProps, "direction">> = (props) => (
	<Trigger {...props} direction="next" />
);

type PaginationItemRenderProps = {
	isSelected: boolean;
	isDisabled: boolean;
	onClick: () => void;
	value: number;
	"aria-current"?: "page";
	"aria-label"?: string;
};

export type PaginationItemProps = {
	/** The value of the pagination item. */
	value: number;
	/** Whether the pagination item is the current page. */
	isCurrent: boolean;
	/** The children of the pagination item. Can be a render prop or a valid element. */
	children?: ReactNode | ((props: PaginationItemRenderProps) => ReactNode);
	/** The style object of the pagination item. */
	style?: CSSProperties;
	/** The class name of the pagination item. */
	className?: string | ((args: { isSelected: boolean }) => string);
	/** The aria label of the pagination item. */
	ariaLabel?: string;
	/** If true, the child element will be cloned and passed down the prop of the item. */
	asChild?: boolean;
};

const PaginationItem = ({
	value,
	isCurrent,
	children,
	style,
	className,
	ariaLabel,
	asChild = false,
}: PaginationItemProps) => {
	const { onPageChange, canNavigateTo: canNavigate } = usePaginationContext();

	const isSelected = isCurrent;
	// A página atual continua focável e anunciada; acioná-la não emite nada.
	const isDisabled = !(isCurrent || canNavigate(value));
	const label = ariaLabel || `Ir para a página ${value}`;
	const ariaCurrent = isCurrent ? ("page" as const) : undefined;

	const handleClick = () => {
		onPageChange(value);
	};

	const computedClassName =
		typeof className === "function" ? className({ isSelected }) : className;

	// If the children is a render prop, we need to pass the necessary props to the render prop.
	if (typeof children === "function") {
		return (
			<>
				{children({
					isSelected,
					isDisabled,
					onClick: handleClick,
					value,
					"aria-current": ariaCurrent,
					"aria-label": label,
				})}
			</>
		);
	}

	// If the children is a valid element, we need to clone it and pass the necessary props to the cloned element.
	if (asChild && isValidElement(children)) {
		return cloneElement(children, {
			onClick: handleClick,
			disabled: isDisabled || undefined,
			isDisabled,
			"aria-current": ariaCurrent,
			"aria-label": label,
			style: {
				...(children.props as HTMLAttributes<HTMLElement>).style,
				...style,
			},
			className:
				[
					computedClassName,
					(children.props as HTMLAttributes<HTMLElement>).className,
				]
					.filter(Boolean)
					.join(" ") || undefined,
		} as HTMLAttributes<HTMLElement>);
	}

	return (
		<button
			aria-current={ariaCurrent}
			aria-label={label}
			className={computedClassName}
			disabled={isDisabled}
			onClick={handleClick}
			style={style}
			type="button"
		>
			{children}
		</button>
	);
};

type PaginationEllipsisProps = {
	key: number;
	children?: ReactNode;
	style?: CSSProperties;
	className?: string | (() => string);
};

const PaginationEllipsis: FC<PaginationEllipsisProps> = ({
	children,
	style,
	className,
}) => {
	const computedClassName =
		typeof className === "function" ? className() : className;

	return (
		<span aria-hidden="true" className={computedClassName} style={style}>
			{children}
		</span>
	);
};

type PaginationContextComponentProps = {
	children: (pagination: PaginationContextType) => ReactNode;
};

const PaginationContextComponent: FC<PaginationContextComponentProps> = ({
	children,
}) => {
	const context = usePaginationContext();

	return <>{children(context)}</>;
};

export type { PaginationEllipsisType, PaginationPage };

export const Pagination = {
	Root: PaginationRoot,
	PrevTrigger: PaginationPrevTrigger,
	NextTrigger: PaginationNextTrigger,
	Item: PaginationItem,
	Ellipsis: PaginationEllipsis,
	Context: PaginationContextComponent,
};
